import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { InputNumber, Input, notification, Checkbox, Tooltip } from 'antd';
import { DeleteOutlined, DisconnectOutlined } from '@ant-design/icons';
import { ThemeModal } from '../Styles/Theme';
import {
  NestedQuotaModalContent,
  NestedQuotaActions,
} from './CreateCampaignStyle';

// Field configuration - same as QuotaModal for label resolution
const FIELD_CONFIG = {
  gender: { code: 211, type: 'condition_codes', label: 'Gender' },
  age: { code: 212, type: 'range_sets', units: 311, label: 'Age' },
  income: { code: 213, type: 'range_sets', units: 321, label: 'Income' },
  race: { code: 244, type: 'condition_codes', label: 'Race / Ethnicity' },
  hispanic_origin: { code: 245, type: 'condition_codes', label: 'Hispanic Origin' },
  relationship: { code: 217, type: 'condition_codes', label: 'Relationship Status' },
  children: { code: 232, type: 'condition_codes', label: 'Children' },
  employment: { code: 215, type: 'condition_codes', label: 'Employment' },
  education: { code: 216, type: 'condition_codes', label: 'Education' },
  device: { code: 219, type: 'condition_codes', label: 'Device' },
  region: { code: 223, type: 'condition_codes', label: 'Region' },
  division: { code: 224, type: 'condition_codes', label: 'Division' },
  state: { code: 225, type: 'condition_codes', label: 'State' },
  msa: { code: 227, type: 'condition_codes', label: 'MSA / CBSA' },
  csa: { code: 226, type: 'condition_codes', label: 'CSA' },
  county: { code: 228, type: 'condition_codes', label: 'County' },
  dma: { code: 231, type: 'condition_codes', label: 'DMA' },
  zipcode: { code: 229, type: 'condition_codes', label: 'Zipcode' },
};

const MAX_NESTED_FIELDS = 4;

const getFieldDimensions = (field, dataSources) => {
  const { formValues, demographicOptions, quotas, advancedTargeting, ageRange, incomeRange } = dataSources;
  const config = FIELD_CONFIG[field];

  // AT fields
  const atMatch = field.match(/^at_(\d+)$/);
  if (atMatch) {
    const qualCode = parseInt(atMatch[1], 10);
    const atItem = advancedTargeting.find(a => a.qualification_code === qualCode);
    if (!atItem) return [];
    return (atItem.condition_codes || []).map((code, idx) => ({
      field,
      qualification_code: qualCode,
      condition_code: code,
      label: atItem.selected_labels?.[idx] || code,
    }));
  }

  if (!config) return [];

  // Range fields: use individual quota bands
  if (config.type === 'range_sets') {
    const fieldQuota = quotas.find(q => q.field === field);
    if (fieldQuota && fieldQuota.entries?.length > 0) {
      return fieldQuota.entries.map(e => ({
        field,
        qualification_code: config.code,
        range_from: e.range_from,
        range_to: e.range_to,
        units: e.units || config.units,
        label: field === 'income'
          ? `$${e.range_from?.toLocaleString()} - $${e.range_to?.toLocaleString()}`
          : `${e.range_from} - ${e.range_to}`,
      }));
    }
    // Fallback: whole range as one dimension
    const range = field === 'age' ? ageRange : incomeRange;
    if (range && range.min != null && range.max != null) {
      return [{
        field,
        qualification_code: config.code,
        range_from: range.min,
        range_to: range.max,
        units: config.units,
        label: field === 'income'
          ? `$${range.min?.toLocaleString()} - $${range.max?.toLocaleString()}`
          : `${range.min} - ${range.max}`,
      }];
    }
    return [];
  }

  // Condition_codes fields
  const selectedValues = formValues[field] || [];
  const options = demographicOptions[field] || [];
  return selectedValues.map(val => {
    const opt = options.find(o => o.value === val);
    return {
      field,
      qualification_code: config.code,
      condition_code: opt?.condition_code || val,
      label: opt?.label || val,
    };
  });
};

const generateCrossProduct = (fieldDimensionsArray) => {
  if (fieldDimensionsArray.length === 0) return [];
  if (fieldDimensionsArray.length === 1) {
    return fieldDimensionsArray[0].map(dim => ({
      criteria: [dim],
      quota_title: dim.label,
    }));
  }

  const [first, ...rest] = fieldDimensionsArray;
  const restProduct = generateCrossProduct(rest);

  const result = [];
  for (const dim of first) {
    for (const cell of restProduct) {
      result.push({
        criteria: [dim, ...cell.criteria],
        quota_title: `${dim.label}, ${cell.quota_title}`,
      });
    }
  }
  return result;
};

const NestedQuotaModal = ({
  visible,
  onClose,
  onSave,
  editingNest,
  totalResponses,
  formValues,
  demographicOptions,
  quotas,
  advancedTargeting,
  ageRange,
  incomeRange,
}) => {
  const [selectedFields, setSelectedFields] = useState([]);
  const [cells, setCells] = useState([]);
  const [selectedCellIndices, setSelectedCellIndices] = useState([]);
  const [nextGroupNumber, setNextGroupNumber] = useState(1);
  const [combineMode, setCombineMode] = useState(false);

  const dataSources = useMemo(() => ({
    formValues, demographicOptions, quotas, advancedTargeting, ageRange, incomeRange,
  }), [formValues, demographicOptions, quotas, advancedTargeting, ageRange, incomeRange]);

  // Available fields — only those that have an existing individual quota
  const availableFields = useMemo(() => {
    const quotaFields = new Set((quotas || []).map(q => q.field));
    const fields = [];

    for (const [fieldName, config] of Object.entries(FIELD_CONFIG)) {
      if (!quotaFields.has(fieldName)) continue;
      const dims = getFieldDimensions(fieldName, dataSources);
      if (dims.length > 0) {
        fields.push({ field: fieldName, label: config.label, dimensionCount: dims.length });
      }
    }

    for (const at of advancedTargeting) {
      const atField = `at_${at.qualification_code}`;
      if (!quotaFields.has(atField)) continue;
      const dims = getFieldDimensions(atField, dataSources);
      if (dims.length > 0) {
        fields.push({
          field: atField,
          label: at.qualification_name || `AT ${at.qualification_code}`,
          dimensionCount: dims.length,
        });
      }
    }

    return fields;
  }, [dataSources, advancedTargeting, quotas]);

  // Initialize on open
  useEffect(() => {
    if (!visible) {
      setSelectedFields([]);
      setCells([]);
      setSelectedCellIndices([]);
      setNextGroupNumber(1);
      setCombineMode(false);
      return;
    }

    if (editingNest) {
      const rawFields = editingNest.fields || [];
      // Filter to only include fields that still have individual quotas
      const quotaFieldSet = new Set((quotas || []).map(q => q.field));
      const fields = rawFields.filter(f => quotaFieldSet.has(f));
      setSelectedFields(fields);
      setSelectedCellIndices([]);

      if (fields.length === 0) {
        setCells([]);
      } else {
      const existingCells = editingNest.cells || [];

      if (existingCells.length === 0) {
        // Cells were cleared by cleanup — regenerate from remaining fields
        const dims = fields.map(f => getFieldDimensions(f, dataSources));
        const crossProduct = generateCrossProduct(dims);
        crossProduct.forEach(c => { c.required_count = 0; });
        setCells(crossProduct);
      } else if (existingCells.some(c => c.is_combined)) {
        // Load cells directly to preserve combined groups
        // Regenerate uncombined cells from cross-product, keep combined as-is
        const dims = fields.map(f => getFieldDimensions(f, dataSources));
        const crossProduct = generateCrossProduct(dims);
        const existingCountMap = new Map();
        const combinedCells = [];

        // Separate combined cells and build count map for uncombined
        for (const cell of existingCells) {
          if (cell.is_combined) {
            combinedCells.push(cell);
            // Track which quota_titles are inside combined groups
            if (cell.sub_cells) {
              cell.sub_cells.forEach(sc => existingCountMap.set(sc.quota_title, -1));
            }
          } else {
            existingCountMap.set(cell.quota_title, cell.required_count);
          }
        }

        // Build final cells: uncombined from cross-product + combined groups in order
        const uncombinedCells = crossProduct
          .filter(c => existingCountMap.get(c.quota_title) !== -1) // exclude ones inside combined groups
          .map(c => ({
            ...c,
            required_count: existingCountMap.get(c.quota_title) ?? 0,
          }));

        // Interleave: put combined cells in their original positions
        const finalCells = [];
        let uncombIdx = 0;
        for (const cell of existingCells) {
          if (cell.is_combined) {
            finalCells.push(cell);
          } else {
            // Find matching uncombined cell
            const match = uncombinedCells.find((uc, i) => i >= uncombIdx && uc.quota_title === cell.quota_title);
            if (match) {
              finalCells.push(match);
              uncombIdx = uncombinedCells.indexOf(match) + 1;
            }
          }
        }
        // Add any new cross-product cells not in existing
        for (const uc of uncombinedCells) {
          if (!finalCells.some(fc => fc.quota_title === uc.quota_title)) {
            finalCells.push(uc);
          }
        }

        setCells(finalCells.length > 0 ? finalCells : existingCells);

        const maxGroup = existingCells
          .filter(c => c.is_combined && c.combined_label)
          .reduce((max, c) => {
            const match = c.combined_label.match(/Quota Group (\d+)/);
            return match ? Math.max(max, parseInt(match[1], 10)) : max;
          }, 0);
        setNextGroupNumber(maxGroup + 1);
      } else {
        // Regenerate cells from current dimensions, preserving counts
        const dims = fields.map(f => getFieldDimensions(f, dataSources));
        const crossProduct = generateCrossProduct(dims);
        const existingMap = new Map();
        existingCells.forEach(c => existingMap.set(c.quota_title, c.required_count));
        crossProduct.forEach(c => { c.required_count = existingMap.get(c.quota_title) ?? 0; });
        setCells(crossProduct);
      }
      }
    }
  }, [visible, editingNest, dataSources]);

  // Regenerate cells when selectedFields changes (after initial load)
  const regenerateCells = useCallback((fields) => {
    setSelectedCellIndices([]);
    setCombineMode(false);
    if (fields.length < 1) {
      setCells([]);
      return;
    }
    const dims = fields.map(f => getFieldDimensions(f, dataSources));
    const crossProduct = generateCrossProduct(dims);
    // Preserve existing counts by quota_title
    setCells(prevCells => {
      const existingMap = new Map();
      prevCells.forEach(c => existingMap.set(c.quota_title, c.required_count));
      return crossProduct.map(c => ({
        ...c,
        required_count: existingMap.get(c.quota_title) ?? 0,
      }));
    });
  }, [dataSources]);

  const handleFieldToggle = (field) => {
    setSelectedFields(prev => {
      const next = prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field];
      regenerateCells(next);
      return next;
    });
  };

  const handleCountChange = (index, value) => {
    setCells(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], required_count: value || 0 };
      return updated;
    });
  };

  const handleEvenSplit = () => {
    if (!totalResponses || cells.length === 0) return;
    const perCell = Math.floor(totalResponses / cells.length);
    const remainder = totalResponses % cells.length;
    setCells(prev => prev.map((cell, idx) => ({
      ...cell,
      required_count: perCell + (idx < remainder ? 1 : 0),
    })));
  };

  const handleClearAll = () => {
    setCells(prev => prev.map(cell => ({ ...cell, required_count: 0 })));
  };

  const handleDeleteCell = (index) => {
    if (cells.length <= 1) {
      notification.warning({ message: 'Cannot Delete', description: 'Must keep at least one cell.' });
      return;
    }
    setCells(prev => prev.filter((_, i) => i !== index));
    setSelectedCellIndices(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  const handleToggleCellSelect = (idx) => {
    setSelectedCellIndices(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const handleSelectAllCells = (checked) => {
    if (checked) {
      // Select all uncombined cell indices
      const uncombinedIndices = cells
        .map((c, i) => (!c.is_combined ? i : null))
        .filter(i => i !== null);
      setSelectedCellIndices(uncombinedIndices);
    } else {
      setSelectedCellIndices([]);
    }
  };

  const handleCombineQuotas = () => {
    if (selectedCellIndices.length < 2) return;

    // PS rule: can't combine ALL uncombined cells into one group
    const uncombinedCount = cells.filter(c => !c.is_combined).length;
    if (selectedCellIndices.length >= uncombinedCount) {
      notification.warning({
        message: 'Cannot Combine All',
        description: 'You cannot select all conditions in a single group. Please unselect at least one cell.',
      });
      return;
    }

    const selected = [...selectedCellIndices].sort((a, b) => a - b);
    const selectedCells = selected.map(i => cells[i]);
    const totalCount = selectedCells.reduce((sum, c) => sum + (c.required_count || 0), 0);
    const combinedCell = {
      quota_title: `Quota Group ${nextGroupNumber}`,
      required_count: totalCount,
      criteria: selectedCells.flatMap(c => c.criteria),
      is_combined: true,
      combined_label: `Quota Group ${nextGroupNumber}`,
      sub_cells: selectedCells.map(c => ({ quota_title: c.quota_title, criteria: c.criteria })),
    };
    const firstIdx = selected[0];
    const remaining = cells.filter((_, i) => !selected.includes(i));
    remaining.splice(firstIdx, 0, combinedCell);
    setCells(remaining);
    setSelectedCellIndices([]);
    setCombineMode(false);
    setNextGroupNumber(prev => prev + 1);
  };

  const handleCombinedLabelChange = (index, newLabel) => {
    setCells(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        combined_label: newLabel,
        quota_title: newLabel,
      };
      return updated;
    });
  };

  const handleUncombine = (idx) => {
    const cell = cells[idx];
    if (!cell.is_combined || !cell.sub_cells) return;
    const count = cell.required_count || 0;
    const perCell = Math.floor(count / cell.sub_cells.length);
    const remainder = count % cell.sub_cells.length;
    const restored = cell.sub_cells.map((sc, i) => ({
      ...sc,
      required_count: perCell + (i < remainder ? 1 : 0),
    }));
    setCells(prev => {
      const updated = [...prev];
      updated.splice(idx, 1, ...restored);
      return updated;
    });
    setSelectedCellIndices([]);
  };

  const totalCount = useMemo(() => {
    return cells.reduce((sum, cell) => sum + (cell.required_count || 0), 0);
  }, [cells]);

  const exceedsTotal = totalResponses && totalCount > totalResponses;
  const isUnderTotal = totalResponses && totalCount < totalResponses;

  const handleSave = () => {
    if (selectedFields.length < 1) {
      notification.warning({
        message: 'Selection Required',
        description: 'Please select at least 1 field to create a nested quota.',
      });
      return;
    }

    const validCells = cells.filter(c => c.required_count > 0);
    if (validCells.length === 0) {
      notification.warning({
        message: 'Validation',
        description: 'Please enter at least one cell count greater than 0.',
      });
      return;
    }

    if (exceedsTotal) {
      notification.error({
        message: 'Validation Error',
        description: `Total nested quota (${totalCount}) cannot exceed total responses (${totalResponses}).`,
      });
      return;
    }

    const groupKey = editingNest?.group_key || `nest_${Date.now()}`;

    onSave({
      group_key: groupKey,
      fields: selectedFields,
      cells: validCells.map(cell => ({
        quota_title: cell.quota_title,
        required_count: cell.required_count,
        criteria: cell.criteria,
        is_combined: cell.is_combined || false,
        combined_label: cell.combined_label || null,
        sub_cells: cell.is_combined ? (cell.sub_cells || []) : [],
      })),
    });
  };

  const canSave = selectedFields.length >= 1 && cells.some(c => c.required_count > 0) && !exceedsTotal;

  const getFieldLabel = (field) => {
    if (FIELD_CONFIG[field]) return FIELD_CONFIG[field].label;
    const atMatch = field.match(/^at_(\d+)$/);
    if (atMatch) {
      const qualCode = parseInt(atMatch[1], 10);
      const atItem = advancedTargeting.find(a => a.qualification_code === qualCode);
      return atItem?.qualification_name || `AT ${qualCode}`;
    }
    return field;
  };

  return (
    <ThemeModal
      title="Quota Nesting"
      open={visible}
      onCancel={onClose}
      onOk={handleSave}
      width={700}
      destroyOnClose
      centered
      okText="Save"
      cancelText="Cancel"
      okButtonProps={{ disabled: !canSave }}
    >
      <NestedQuotaModalContent>
        {/* Field selection (top) */}
        <div className="field-selection">
          <div className="field-selection-label">
            Select fields to nest (1 or more):
          </div>
          <div className="field-checkbox-list">
            {availableFields.map(af => {
              const isChecked = selectedFields.includes(af.field);
              const atLimit = !isChecked && selectedFields.length >= MAX_NESTED_FIELDS;

              const checkbox = (
                <Checkbox
                  key={af.field}
                  checked={isChecked}
                  onChange={() => handleFieldToggle(af.field)}
                  className="field-checkbox-item"
                  disabled={atLimit}
                >
                  {af.label}
                  <span className="field-option-count">
                    ({af.dimensionCount})
                  </span>
                </Checkbox>
              );

              return atLimit ? (
                <Tooltip key={af.field} title={`Maximum of ${MAX_NESTED_FIELDS} qualifications allowed for nesting`}>
                  <span>{checkbox}</span>
                </Tooltip>
              ) : checkbox;
            })}
          </div>

          {availableFields.length === 0 && (
            <div className="no-fields-hint">
              No fields have quotas yet. Add quotas to demographic fields first.
            </div>
          )}

          {selectedFields.length >= MAX_NESTED_FIELDS && (
            <div className="cell-count-warning">
              Maximum of {MAX_NESTED_FIELDS} qualifications reached ({cells.length} cells).
            </div>
          )}
        </div>

        {/* Cell table (bottom) */}
        {cells.length === 0 ? (
          <div className="no-cells-hint">
            {selectedFields.length < 1
              ? 'Select at least 1 field above to generate quota cells.'
              : 'No cells to display.'}
          </div>
        ) : (
          <>
            <div className="cell-table-wrapper">
              <table className="cell-table">
                <thead>
                  <tr>
                    {combineMode && (
                      <th className="check-col">
                        <Checkbox
                          checked={selectedCellIndices.length > 0 && selectedCellIndices.length === cells.filter(c => !c.is_combined).length}
                          indeterminate={selectedCellIndices.length > 0 && selectedCellIndices.length < cells.filter(c => !c.is_combined).length}
                          onChange={(e) => handleSelectAllCells(e.target.checked)}
                        />
                      </th>
                    )}
                    {selectedFields.map(field => (
                      <th key={field}>{getFieldLabel(field)}</th>
                    ))}
                    <th className="count-col"># Completes</th>
                    <th className="action-col"></th>
                  </tr>
                </thead>
                <tbody>
                  {cells.map((cell, cellIdx) => (
                    cell.is_combined ? (
                      <tr key={cellIdx} className="combined-group-row">
                        {combineMode && <td className="check-col"></td>}
                        <td colSpan={selectedFields.length}>
                          <Input
                            className="combined-label-input"
                            value={cell.combined_label || cell.quota_title}
                            onChange={(e) => handleCombinedLabelChange(cellIdx, e.target.value)}
                            size="small"
                            bordered={false}
                            placeholder="Group name"
                          />
                        </td>
                        <td className="count-col">
                          <InputNumber
                            value={cell.required_count}
                            onChange={(value) => handleCountChange(cellIdx, value)}
                            min={0}
                            max={totalResponses || 999999}
                            placeholder="0"
                            controls={false}
                            size="small"
                          />
                        </td>
                        <td className="action-col">
                          <DisconnectOutlined className="cell-delete-icon" onClick={() => handleUncombine(cellIdx)} />
                        </td>
                      </tr>
                    ) : (
                      <tr key={cellIdx}>
                        {combineMode && (
                          <td className="check-col">
                            <Checkbox
                              checked={selectedCellIndices.includes(cellIdx)}
                              onChange={() => handleToggleCellSelect(cellIdx)}
                            />
                          </td>
                        )}
                        {cell.criteria.map((criterion, critIdx) => (
                          <td key={critIdx}>{criterion.label}</td>
                        ))}
                        <td className="count-col">
                          <InputNumber
                            value={cell.required_count}
                            onChange={(value) => handleCountChange(cellIdx, value)}
                            min={0}
                            max={totalResponses || 999999}
                            placeholder="0"
                            controls={false}
                            size="small"
                          />
                        </td>
                        <td className="action-col">
                          {!combineMode && (
                            <DeleteOutlined className="cell-delete-icon" onClick={() => handleDeleteCell(cellIdx)} />
                          )}
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>

            <div className={`cell-total-row ${exceedsTotal ? 'error' : isUnderTotal && !exceedsTotal ? 'warning' : ''}`}>
              <span>Total</span>
              <span>
                {totalCount}
                {totalResponses && <span style={{ color: '#485066', fontWeight: 400 }}> / {totalResponses}</span>}
              </span>
            </div>

            {exceedsTotal && (
              <div className="quota-error">
                Total cannot exceed total responses ({totalResponses}).
              </div>
            )}

            {isUnderTotal && !exceedsTotal && (
              <div className="quota-info">
                Quota total ({totalCount}) is less than responses ({totalResponses}).
              </div>
            )}

            <NestedQuotaActions>
              {combineMode ? (
                <>
                  <button
                    type="button"
                    className="action-btn combine-btn"
                    onClick={handleCombineQuotas}
                    disabled={selectedCellIndices.length < 2}
                  >
                    Combine Selected ({selectedCellIndices.length})
                  </button>
                  <button
                    type="button"
                    className="action-btn clear-btn"
                    onClick={() => { setCombineMode(false); setSelectedCellIndices([]); }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {cells.filter(c => !c.is_combined).length > 1 && (
                    <button type="button" className="action-btn" onClick={() => setCombineMode(true)}>
                      Combine
                    </button>
                  )}
                  <button type="button" className="action-btn" onClick={handleEvenSplit} disabled={!totalResponses}>
                    Even Split
                  </button>
                  <button type="button" className="action-btn clear-btn" onClick={handleClearAll}>
                    Clear All
                  </button>
                </>
              )}
            </NestedQuotaActions>
          </>
        )}

        {cells.length > 0 && (
          <div className="qbp-warning">
            Note: Nested quotas activate Quota-Based Pricing (QBP) on PureSpectrum, which may affect costs.
          </div>
        )}
      </NestedQuotaModalContent>
    </ThemeModal>
  );
};

export default NestedQuotaModal;
